/** Markers that begin a block-style statistics dump inside a narrative ("Overall — …", "By Grade:
 *  …", "Students below 85% (31 total): …"). Everything from the first match on is re-rendered as
 *  tables, so the chat bubble keeps only the prose above it. */
const DUMP_MARKERS: RegExp[] = [
  /^\s*Attendance report for .*(?:to|–|-).*$/m,
  /^\s*Overall\s*[—–-]\s*current period/m,
  /^\s*By (?:Grade|Section|Class|Gender):/m,
  /^\s*Lowest[- ]attendance classes:/m,
  /^\s*Students below \d+(?:\.\d+)?%/m,
]

/** Separators the backend puts between a lead clause and the inline record list it appends to it
 *  ("Attendance overview for <range> — Grade 10 / Section A: 88.39% (10 students); …"). */
const LEAD_CLAUSE_SEPARATORS = [' — ', ' – ', ': ']

const MIN_LEAD_CLAUSE_LENGTH = 12

/**
 * True for a line that is really a record list in prose form: several `;`-joined entries carrying
 * percentages. Those are exactly the lines a table renders far better than a paragraph.
 */
function isInlineRecordList(line: string): boolean {
  const semicolons = (line.match(/;/g) ?? []).length
  const percents = (line.match(/%/g) ?? []).length
  return semicolons >= 2 && percents >= 2
}

/**
 * Keeps the lead clause of an inline record list ("Attendance overview for June 22 to July 21")
 * and drops the records themselves. Returns `null` when there is no meaningful lead clause, i.e.
 * the whole line is data.
 */
function leadClauseOf(line: string): string | null {
  for (const separator of LEAD_CLAUSE_SEPARATORS) {
    const idx = line.indexOf(separator)
    if (idx < MIN_LEAD_CLAUSE_LENGTH) continue
    const lead = line.slice(0, idx).trim()
    if (lead.includes(';')) continue
    return /[.!?]$/.test(lead) ? lead : `${lead}.`
  }
  return null
}

/**
 * Splits a narrative into the human-written part (executive summary, recommendations, caveats) and
 * the trailing block-style statistics dump. Returns the full text as `summary` when no dump marker
 * is found, or when the dump starts at the very top — never blank out the bubble.
 */
export function splitAttendanceNarrativeDump(narrative: string): {summary: string; dump: string} {
  const text = (narrative || '').trim()
  if (!text) return {summary: '', dump: ''}

  let cutIndex = -1
  for (const marker of DUMP_MARKERS) {
    const match = marker.exec(text)
    if (!match || match.index <= 0) continue
    if (cutIndex === -1 || match.index < cutIndex) cutIndex = match.index
  }

  if (cutIndex <= 0) return {summary: text, dump: ''}

  const summary = text.slice(0, cutIndex).trim()
  if (!summary) return {summary: text, dump: ''}

  return {summary, dump: text.slice(cutIndex).trim()}
}

/**
 * Strips the numbers a structured view already shows out of a narrative: the trailing block dump,
 * plus any line that is just an inline `;`-joined record list. Returns the text untouched when no
 * structured view is rendering (`hasStructuredView: false`) — the prose is the only answer then —
 * and falls back to the original whenever condensing would empty the bubble.
 */
export function condenseAttendanceNarrative(
  narrative: string,
  options: {hasStructuredView: boolean}
): string {
  const text = (narrative || '').trim()
  if (!text || !options.hasStructuredView) return text

  const {summary} = splitAttendanceNarrativeDump(text)

  const condensed = summary
    .split('\n')
    .map((line) => {
      const trimmed = line.trim()
      if (!isInlineRecordList(trimmed)) return line
      return leadClauseOf(trimmed) ?? ''
    })
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()

  return condensed || summary || text
}
